import { html, nothing } from "lit";
import type { LocalResourcesHarnessFactor, LocalResourcesSnapshot } from "../types.ts";

const CURSOR_REFERENCE: Record<string, string> = {
  inference: "Often cloud GPUs (fast host)",
  turns_per_task: "Fewer, more direct edits",
  extra_work: "Smaller scoped context",
  streaming: "Streaming UI usually feels faster",
  topology: "Direct host process",
};

function titleForFactor(id: LocalResourcesHarnessFactor["id"]): string {
  switch (id) {
    case "inference":
      return "Inference";
    case "turns_per_task":
      return "Turns per task";
    case "extra_work":
      return "Extra work per turn";
    case "streaming":
      return "Streaming";
    case "topology":
      return "Topology";
    case "ollama_parallel":
      return "Ollama parallel saturation";
    case "memory_pressure":
      return "Memory pressure";
    case "gateway_lane":
      return "Gateway main lane";
    case "model_not_loaded":
      return "Model load state";
    case "throughput":
      return "Inference throughput";
    default:
      return id;
  }
}

function severityClass(severity: LocalResourcesHarnessFactor["severity"]): string {
  if (severity === "bad") {
    return "bad";
  }
  if (severity === "warn") {
    return "warn";
  }
  return "ok";
}

function resolveOllamaModelForFix(
  snapshot: LocalResourcesSnapshot,
  selectedModel: string | null | undefined,
): string | null {
  const selected = selectedModel?.trim() || "";
  if (selected) {
    return selected;
  }
  const fromLastInference = snapshot.harness.lastInference?.model?.trim() || "";
  if (fromLastInference) {
    return fromLastInference;
  }
  const primary = snapshot.harness.agent.primaryModel?.trim() || "";
  if (primary.startsWith("ollama/") && primary.length > "ollama/".length) {
    return primary.slice("ollama/".length);
  }
  return snapshot.ollama.installedModels[0]?.name ?? null;
}

function buildFixText(
  factor: LocalResourcesHarnessFactor,
  snapshot: LocalResourcesSnapshot,
  selectedModel: string | null | undefined,
): string {
  const header = `Factor: ${titleForFactor(factor.id)}\nSeverity: ${factor.severity}\nLive: ${factor.liveValue}\nHint: ${factor.hint}`;
  if (factor.id !== "model_not_loaded") {
    return header;
  }
  const model = resolveOllamaModelForFix(snapshot, selectedModel);
  if (!model) {
    return header;
  }
  return `${header}\n\nTry warming the model:\nollama run ${model} "hi"`;
}

async function copyFixText(text: string): Promise<void> {
  if (!navigator?.clipboard?.writeText) {
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Ignore clipboard write failures (browser permission/user gesture constraints).
  }
}

function renderPrimaryRows(
  factors: LocalResourcesHarnessFactor[],
  snapshot: LocalResourcesSnapshot,
  selectedModel: string | null | undefined,
) {
  const first = factors.filter((f) =>
    ["inference", "turns_per_task", "extra_work", "streaming", "topology"].includes(f.id),
  );
  return first.map(
    (factor) => html`
      <tr>
        <td>${titleForFactor(factor.id)}</td>
        <td>${CURSOR_REFERENCE[factor.id] ?? "—"}</td>
        <td>
          <span class="local-llm-factor-badge local-llm-factor-badge--${severityClass(factor.severity)}">
            ${factor.severity}
          </span>
          <span>${factor.liveValue}</span>
        </td>
        <td>${factor.hint}</td>
        <td>
          ${
            factor.severity === "ok"
              ? html`
                  <span class="muted">—</span>
                `
              : html`
                  <button
                    type="button"
                    class="btn btn--sm"
                    title="Copy suggested fix steps"
                    @click=${() => void copyFixText(buildFixText(factor, snapshot, selectedModel))}
                  >
                    Copy fix
                  </button>
                `
          }
        </td>
      </tr>
    `,
  );
}

function renderAdditionalRows(factors: LocalResourcesHarnessFactor[]) {
  const extra = factors.filter(
    (f) => !["inference", "turns_per_task", "extra_work", "streaming", "topology"].includes(f.id),
  );
  if (extra.length === 0) {
    return nothing;
  }
  return html`
    <details class="local-llm-factor-extra">
      <summary>More bottleneck checks</summary>
      <div class="local-llm-factor-chips">
        ${extra.map(
          (factor) => html`
            <div class="local-llm-factor-chip local-llm-factor-chip--${severityClass(factor.severity)}">
              <strong>${titleForFactor(factor.id)}:</strong> ${factor.liveValue}
              <span class="muted"> — ${factor.hint}</span>
            </div>
          `,
        )}
      </div>
    </details>
  `;
}

export function renderPerformanceFactorsTable(
  snapshot: LocalResourcesSnapshot | null,
  selectedModel?: string | null,
) {
  const factors = snapshot?.harness?.factors ?? [];
  if (!snapshot || factors.length === 0) {
    return nothing;
  }
  return html`
    <section class="local-llm-factors">
      <div class="card-title">Performance factors (Cursor-style comparison)</div>
      <table class="llm-stats-network-table local-llm-factors__table" style="margin-top: 8px;">
        <thead>
          <tr>
            <th>Factor</th>
            <th>Cursor (reference)</th>
            <th>Jaisiu (live)</th>
            <th>Hint</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          ${renderPrimaryRows(factors, snapshot, selectedModel)}
        </tbody>
      </table>
      ${renderAdditionalRows(factors)}
    </section>
  `;
}
